import _ from "lodash";
import { useEffect, useRef } from "react";
import ColorX from "../STATIC/ColorX";
import { LocaleX } from "IZOX/STATIC";

/**
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * 
 * @typedef {Object} xtooltipparams
 * @property {any} children
 * @property {lang} tooltip
 * @property {(rect) => {left, top}} tootipPos
 * @property {Number} fontSize
 * @property {Number} width
 * @property {import("react").CSSProperties} style
 * @property {(left, top) => JSX.Element} renderer - Custom renderer function that returns a React element for the tooltip. Receives left and top as parameters.
 * @property {boolean} noWrap - Option to disable wrapping and allow tooltip to enlarge without wrapping.
 * @property {boolean} scrollOnOverflow - Allow scrolling when content exceeds viewport (vertical and/or horizontal).
 * @property {number|string} maxHeight - Optional maximum height for the tooltip before scrolling (number = px, string accepted as CSS value). Clamped to viewport.
 *
 * 
 * @param {xtooltipparams} param0 
 */
function XTooltip({
  children, 
  tooltip,
  tooltipPos = (rect) => ({
    left: (rect?.x + rect?.width / 2) ?? 0,
    top: (rect?.y + rect?.height) ?? 0
  }),
  style,
  fontSize = 12,
  width = 500,
  renderer = undefined,
  noWrap = false, // NEW: allow disabling wrapping
  scrollOnOverflow = true, // NEW: allow scroll when overflow
  maxHeight = 300 // NEW: user-defined max height before vertical scroll
}){
  const ref = useRef(null);  
  // const nodeElement = document.getElementById("root");
  const portal = document.createElement("span");
  const moveHandlerRef = useRef(null); // track document mousemove handler
  const needScrollRef = useRef(false); // track if scrolling is enabled for current tooltip instance
  const repositionRef = useRef(null); // handler to keep tooltip anchored on scroll/resize
  
  // Helper to compute and apply position based on current trigger + tooltip size
  const applyPosition = (tipEl) => {
    const triggerRect = ref.current?.getBoundingClientRect();
    if(!triggerRect || !tipEl) return;
    // Start with provided tooltipPos logic for consistency
    let basePos = tooltipPos?.(triggerRect) ?? {};
    let newLeft = basePos.left ?? (triggerRect.left + triggerRect.width / 2);
    let newTop = basePos.top ?? (triggerRect.bottom + 8);
    const tipRect = tipEl.getBoundingClientRect();
    // Flip above if would overflow bottom
    if (newTop + tipRect.height + 8 > window.innerHeight) {
      newTop = triggerRect.top - tipRect.height - 8;
    }
    if (newTop < 8) newTop = 8;
    // Horizontal clamp (centered with translateX(-50%))
    const halfW = tipRect.width / 2;
    const minCenter = halfW + 8;
    const maxCenter = window.innerWidth - halfW - 8;
    newLeft = Math.min(Math.max(newLeft, minCenter), maxCenter);
    tipEl.style.left = `${newLeft}px`;
    tipEl.style.top = `${newTop}px`;
  };

  const Clean = () => {
    const thatNode = document.getElementById("izotooltip");
    if(moveHandlerRef.current){
      document.removeEventListener("mousemove", moveHandlerRef.current);
      moveHandlerRef.current = null;
    }
    if(repositionRef.current){
      window.removeEventListener("resize", repositionRef.current);
      document.removeEventListener("scroll", repositionRef.current, true);
      repositionRef.current = null;
    }
    if(thatNode){
      document.body.removeChild(thatNode);
    }
  };

  const onMouseEnter = () => {
    Clean();
    const rect = ref.current?.getBoundingClientRect();
    let {left = 0, top = 0} = tooltipPos?.(rect) ?? {};

    // Prepare tooltip for measurement (respect noWrap)
    portal.id = "izotooltip";
    portal.innerHTML = LocaleX.Parse(tooltip);
    portal.style.cssText = `position: fixed; visibility: hidden; font-size: ${fontSize}px; ${noWrap ? "" : `max-width: ${width}px;`} padding: 4px 5px 2px 5px; font-family: "Roboto", sans-serif; white-space: ${noWrap ? "nowrap" : "pre-wrap"};`;
    document.body.appendChild(portal);
    let tooltipRect = portal.getBoundingClientRect();
    document.body.removeChild(portal);

    // Base position (center bottom of target)
    if (!tooltipPos) {
      left = (rect?.left ?? 0) + (rect?.width ?? 0) / 2;
      top = (rect?.bottom ?? 0) + 8; // 8px gap
    }

    // If would go below viewport, place above
    if (top + tooltipRect.height + 8 > window.innerHeight) {
      top = (rect?.top ?? 0) - tooltipRect.height - 8;
    }
    // Clamp vertically
    if (top < 8) top = 8;

    // Horizontal clamping
    let appliedFullWidth = false;
    if(noWrap){
      // If the single-line tooltip is wider than viewport, cap it and re-measure
      const maxViewportWidth = window.innerWidth - 16; // 8px padding each side
      if(tooltipRect.width > maxViewportWidth){
        portal.style.width = `${maxViewportWidth}px`;
        appliedFullWidth = true;
        tooltipRect = { ...tooltipRect, width: maxViewportWidth };
      }
    }

    // Compute dynamic max-height for scrolling if needed
    const viewportMaxHeight = window.innerHeight - 16; // keep 8px margin top/bottom
    let userMaxHeightPx;
    if(typeof maxHeight === "number") userMaxHeightPx = maxHeight; 
    else if(typeof maxHeight === "string" && maxHeight.endsWith("px")) userMaxHeightPx = parseInt(maxHeight,10);
    // If user provided a numeric limit, clamp it to viewport; otherwise use viewport limit
    const effectiveMaxHeight = userMaxHeightPx ? Math.min(userMaxHeightPx, viewportMaxHeight) : viewportMaxHeight;

    const halfWidth = tooltipRect.width / 2;
    const minCenter = halfWidth + 8;
    const maxCenter = window.innerWidth - halfWidth - 8;
    left = Math.min(Math.max(left, minCenter), maxCenter);

    const baseWrapCSS = noWrap ? "white-space: nowrap;" : "white-space: pre-wrap; word-wrap: break-word;";
    const needVScroll = scrollOnOverflow && tooltipRect.height > effectiveMaxHeight;
    const needHScroll = scrollOnOverflow && noWrap && appliedFullWidth; // horizontal scroll condition
    const needScroll = needVScroll || needHScroll;
    needScrollRef.current = needScroll;
    const scrollYCSS = needVScroll ? `max-height: ${effectiveMaxHeight}px; overflow-y: auto;` : "";
    const horizontalOverflowCSS = noWrap
      ? (scrollOnOverflow
          ? (appliedFullWidth ? "overflow-x: auto;" : "")
          : "overflow: hidden; text-overflow: ellipsis;")
      : "";

    portal.id = "izotooltip";
    portal.innerHTML = LocaleX.Parse(tooltip);
    portal.style.cssText = `background: ${ColorX.CSS("pureDark", 0.5)};
    border-radius: 5px;
    color: ${ColorX.CSS("pureWhite")};
    font-size: ${fontSize}px;
    font-weight: 500;
    position: fixed;
    z-index: 9999;
    left: ${left}px;
    top: ${top}px;
    transform: translateX(-50%);
    padding: 4px 5px 2px 5px;
    pointer-events: auto; /* allow hover over tooltip */
    ${noWrap ? (appliedFullWidth ? "width: calc(100vw - 16px);" : "") : `max-width: ${width}px;`}
    font-family: "Roboto", sans-serif;
    ${baseWrapCSS}
    ${scrollYCSS}
    ${horizontalOverflowCSS}
    opacity: 0; transition: opacity 500ms ease; /* fade-in after correct placement */
    `;
    if(!_.isEmpty(tooltip)){
      // eslint-disable-next-line no-unused-vars
      const el = (renderer ? document.body.appendChild(renderer?.(left, top)) : document.body.appendChild(portal));

      // Recalculate position AFTER mount with actual rendered size (scrollbars may change dimensions)
      const tipEl = document.getElementById("izotooltip");
      if(tipEl){
        applyPosition(tipEl);
        // Force reflow then show
        requestAnimationFrame(() => { tipEl.style.opacity = "1"; });
      }

      // Attach global mousemove only when scrolling tooltip (allow interaction inside tooltip)
      if(needScroll){
        moveHandlerRef.current = (e) => {
          const triggerRect = ref.current?.getBoundingClientRect();
          const tipEl2 = document.getElementById("izotooltip");
          const tipRect2 = tipEl2?.getBoundingClientRect();
          const x = e.clientX; const y = e.clientY;
          const insideTrigger = triggerRect && x >= triggerRect.left && x <= triggerRect.right && y >= triggerRect.top && y <= triggerRect.bottom;
          const insideTooltip = tipRect2 && x >= tipRect2.left && x <= tipRect2.right && y >= tipRect2.top && y <= tipRect2.bottom;
          if(!insideTrigger && !insideTooltip){
            Clean();
          }
        };
        document.addEventListener("mousemove", moveHandlerRef.current);
      }

      // Reposition logic (keeps tooltip anchored under trigger while page scrolls / resizes)
      let rafPending = false;
      repositionRef.current = () => {
        if(rafPending) return;
        rafPending = true;
        requestAnimationFrame(() => {
          rafPending = false;
          const tipEl3 = document.getElementById("izotooltip");
          if(!tipEl3) return;
          applyPosition(tipEl3);
        });
      };
      window.addEventListener("resize", repositionRef.current);
      document.addEventListener("scroll", repositionRef.current, true); // capture to react to ancestor scrolls
    }
  };

  useEffect(() => {
    return () => {
      Clean();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMouseLeave = () => { 
    // If tooltip does NOT need scroll (no overflow), close immediately when leaving trigger
    if(!needScrollRef.current){
      Clean();
    }
  };
  
  return (
    <div ref={ref} style={style}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
    </div>
  );
}

export default XTooltip;